export interface IAutoRentalStructuralContent {
	name?: string
	description?: string
	telephone?: string
	streetAddress?: string
	addressLocality?: string
	addressRegion?: string
	postalCode?: string
	isPickupPoint?: boolean
}

export interface IAutoRentalGeo {
	latitude: number
	longitude: number
}

export interface IAutoRentalJsonldParams {
	url: string
	name?: string
	description?: string
	content?: IAutoRentalStructuralContent
	geo?: IAutoRentalGeo
}

const DEFAULT_PHONE = '+48 794 500 550'
const DEFAULT_LOGO = 'https://carfree.pl/assets/logo-cf-wb.svg'
const CONTACT_EMAIL = 'rezerwacje@carfree.pl'

const hasValue = (value?: string | null): value is string => !!value && value.trim() !== '' && value !== 'null'

export const getAutoRentalId = (url: string) => `${url}#autorental`

export const getAutoRentalKnowsAbout = (content?: IAutoRentalStructuralContent) => {
	const knowsAbout = ['wynajem aut', 'wypożyczalnia samochodów']
	if (content?.isPickupPoint) knowsAbout.push('punkt odbioru samochodów')
	return knowsAbout
}

export const getAutoRentalAdditionalProperty = (content?: IAutoRentalStructuralContent) => {
	if (!content?.isPickupPoint) return undefined

	return [
		{
			'@type': 'PropertyValue',
			name: 'Informacja o odbiorze',
			value: 'W przypadku punktu odbioru wymagana jest wcześniejsza rezerwacja samochodu telefonicznie lub online.',
		},
	]
}

// completeness of the address fields decides physical vs non-physical location, not the isAddress flag
// (CMS data can have isAddress: true with every address field left blank)
const isPhysicalAddress = (content?: IAutoRentalStructuralContent) =>
	hasValue(content?.streetAddress) && hasValue(content?.addressLocality) && hasValue(content?.addressRegion) && hasValue(content?.postalCode)

export const getAutoRentalAddress = (content?: IAutoRentalStructuralContent) => {
	if (!isPhysicalAddress(content)) return undefined

	return {
		'@type': 'PostalAddress',
		streetAddress: content!.streetAddress,
		addressLocality: content!.addressLocality,
		addressRegion: content!.addressRegion,
		postalCode: content!.postalCode,
		addressCountry: 'PL',
	}
}

export const getAutoRentalAreaServed = (content?: IAutoRentalStructuralContent) => {
	if (!hasValue(content?.addressLocality)) return undefined

	return {
		'@type': 'City',
		name: content!.addressLocality,
	}
}

export const getAutoRentalLocation = (geo?: IAutoRentalGeo) => {
	if (!geo || typeof geo.latitude !== 'number' || typeof geo.longitude !== 'number' || Number.isNaN(geo.latitude) || Number.isNaN(geo.longitude)) {
		return undefined
	}

	return {
		'@type': 'Place',
		geo: {
			'@type': 'GeoCoordinates',
			latitude: geo.latitude,
			longitude: geo.longitude,
		},
	}
}

export const getAutoRentalContactPoint = (content?: IAutoRentalStructuralContent) => ({
	'@type': 'ContactPoint',
	contactType: 'customer service',
	telephone: hasValue(content?.telephone) ? content!.telephone : DEFAULT_PHONE,
	email: CONTACT_EMAIL,
})

export const buildAutoRentalJsonld = ({ url, name, description, content, geo }: IAutoRentalJsonldParams) => {
	const jsonld: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'AutoRental',
		'@id': getAutoRentalId(url),
		legalName: 'CarFree',
		vatID: '521 369 56 26',
		name,
		description,
		url,
		logo: DEFAULT_LOGO,
		telephone: hasValue(content?.telephone) ? content!.telephone : DEFAULT_PHONE,
		sameAs: ['https://www.instagram.com/carfree.wypozyczalnia/', 'https://www.facebook.com/CarFree.Wypozyczalnia.Aut/'],
		paymentAccepted: ['Invoice', 'DebitCard', 'CreditCard'],
		openingHoursSpecification: [
			{
				'@type': 'OpeningHoursSpecification',
				dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
				opens: '00:00',
				closes: '23:59',
			},
		],
		priceRange: '$$',
		knowsAbout: getAutoRentalKnowsAbout(content),
		contactPoint: getAutoRentalContactPoint(content),
	}

	const address = getAutoRentalAddress(content)
	if (address) jsonld.address = address

	const location = getAutoRentalLocation(geo)
	if (location) jsonld.location = location

	const areaServed = getAutoRentalAreaServed(content)
	if (areaServed) jsonld.areaServed = areaServed

	const additionalProperty = getAutoRentalAdditionalProperty(content)
	if (additionalProperty) jsonld.additionalProperty = additionalProperty

	return jsonld
}

export const useAutoRentalJsonld = (getParams: () => IAutoRentalJsonldParams | undefined) => {
	useJsonld(() => {
		const params = getParams()
		if (!params?.url) return {}

		return buildAutoRentalJsonld(params)
	})
}
